import { useLocalizedNavigate } from '../../hooks/useLocalizedNavigate';
import { useAppSelector } from '../../store/hooks';
import { EVENT_STATUSES, IEvent } from '../../utils/types';
import Button from '../common/Button/Button';
import UserAvatar from '../common/UserAvatar/UserAvatar';
import { Title, TextField, Icon } from '../common';
import { COLOURS, eventTypeMap } from '../../utils/consts';
import { useTranslation } from '../../hooks/translations/TranslationProvider';
import { formatVoteDateDistance } from '../../utils/helpers';
import { useState } from 'react';
import EventInvitationDialog from './EventRequests/EventInvitations/EventInvitationDialog';

interface EventHeaderProps {
    eventDetails: IEvent;
    hasUserJoined: boolean;
    onShowCancelDialog: () => void;
    onLeaveEvent: () => void;
    onRequestToJoin: () => void;
    onGoToEditPage: () => void;
    refreshEventData: () => void;
}

const EventHeader: React.FC<EventHeaderProps> = ({
    eventDetails,
    hasUserJoined,
    onShowCancelDialog,
    onLeaveEvent,
    onRequestToJoin,
    onGoToEditPage,
    refreshEventData,
}) => {
    const navigate = useLocalizedNavigate();
    const { t } = useTranslation();
    const [showInvitationDialog, setShowInvitationDialog] = useState(false);

    const auth = useAppSelector(state => state.auth);
    const language = auth.authData?.language;

    const isOwner = eventDetails.owner.id === auth.authData?.id;

    const showJoinRequestButton =
        !hasUserJoined &&
        !isOwner &&
        eventDetails.isPrivate &&
        !eventDetails.joinRequests.some(
            req => req.user.id === auth.authData?.id
        ) &&
        !eventDetails.invitations.some(
            inv => inv.user.id === auth.authData?.id
        );

    function onCreateInvitationClick(team: string) {
        setShowInvitationDialog(true);
    }

    const userClickHandler = (userId: string) => {
        navigate(`/users/${userId}`);
    };

    if (
        eventDetails.status === EVENT_STATUSES.CANCELLED ||
        eventDetails.status === EVENT_STATUSES.CANCELLED_BY_SYSTEM
    ) {
        return (
            <div className="event-header">
                <div className="event-header-info">
                    <UserAvatar
                        size="l"
                        imageSrc={eventDetails.owner.avatarUrl}
                        averageRating={eventDetails.owner.averageRating}
                        onClick={() => userClickHandler(eventDetails.owner.id)}
                    />
                    <div className="event-details-subheader">
                        <div className="event-details-header-info">
                            <Title
                                text={`${eventTypeMap[eventDetails.type]} - ${
                                    eventDetails.place
                                }`}
                                size="l"
                            />
                            <TextField size="m" color="subtitle">
                                {`${t('common.createdBy')} ${
                                    eventDetails.owner.username
                                }`}
                            </TextField>
                        </div>
                    </div>
                </div>
                <div className="event-details-actions">
                    <Icon name="bx-error" color={COLOURS.RED} />
                    <TextField color="red">
                        {eventDetails.status === EVENT_STATUSES.CANCELLED &&
                            t('eventDetails.eventCancelledByOwner')}
                        {eventDetails.status ===
                            EVENT_STATUSES.CANCELLED_BY_SYSTEM &&
                            t('eventDetails.eventCancelledBySystem')}
                    </TextField>
                </div>
            </div>
        );
    }

    return (
        <div className="event-header">
            <div className="event-header-info">
                <UserAvatar
                    size="l"
                    imageSrc={eventDetails.owner.avatarUrl}
                    averageRating={eventDetails.owner.averageRating}
                    onClick={() => userClickHandler(eventDetails.owner.id)}
                />
                <div className="event-details-subheader">
                    <div className="event-details-header-info">
                        <Title
                            text={`${eventTypeMap[eventDetails.type]} - ${
                                eventDetails.place
                            }`}
                            size="l"
                        />
                        <TextField size="m" color="subtitle">
                            {`${t('common.createdBy')} ${
                                eventDetails.owner.username
                            }`}
                        </TextField>
                        {eventDetails.status === EVENT_STATUSES.VOTE_PHASE && (
                            <div className="vote-end-container">
                                <TextField size="m" color="green">{`${t(
                                    'common.voteEnds'
                                )} ${formatVoteDateDistance(
                                    eventDetails.voteEndDate!,
                                    language
                                )}`}</TextField>
                            </div>
                        )}
                    </div>
                </div>
            </div>
            {eventDetails.status !== EVENT_STATUSES.VOTE_PHASE &&
                eventDetails.status !== EVENT_STATUSES.CLOSED && (
                    <div className="event-details-actions">
                        {isOwner && (
                            <>
                                <Button
                                    text={t('eventHeader.invite')}
                                    variant="transparent"
                                    iconName="bx-user-plus"
                                    textSize="xs"
                                    size="sm"
                                    onClick={onCreateInvitationClick}
                                />
                                <Button
                                    text={t('eventHeader.edit')}
                                    variant="transparent"
                                    iconName="bx-edit"
                                    textSize="xs"
                                    onClick={onGoToEditPage}
                                />
                                <Button
                                    text={t('eventHeader.cancel')}
                                    variant="transparent-red"
                                    iconName="bx-trash"
                                    textSize="xs"
                                    onClick={onShowCancelDialog}
                                />
                            </>
                        )}
                        {hasUserJoined && (
                            <Button
                                text={t('eventHeader.leave')}
                                variant="transparent-red"
                                iconName="bx-door-open"
                                textSize="xs"
                                onClick={onLeaveEvent}
                            />
                        )}
                        {showJoinRequestButton && (
                            <Button
                                text={t('common.requestJoin')}
                                onClick={onRequestToJoin}
                                variant="green"
                                size="sm"
                            />
                        )}
                    </div>
                )}
            <EventInvitationDialog
                isOpen={showInvitationDialog}
                eventDetails={eventDetails}
                onClose={() => setShowInvitationDialog(false)}
                isOwner={isOwner}
                refreshEventData={refreshEventData}
            />
        </div>
    );
};

export default EventHeader;
